/**
 * Reusable "Notify When Available" back-in-stock component.
 *
 * Theme-level, not scoped to any single product. Any product page can
 * use it: see snippets/notify-when-available.liquid for the markup and
 * wiring instructions.
 *
 * Opening/closing the modal itself is handled entirely by the theme's
 * existing native modal-dialog/modal-opener components (already used
 * elsewhere for size charts and product popups) - this script only
 * keeps the modal's content in sync with whatever variant is currently
 * selected, and submits the subscription directly to Klaviyo's official
 * public "Create Client Back In Stock Subscription" API:
 * https://developers.klaviyo.com/en/reference/create_client_back_in_stock_subscription
 *
 * This is a real Klaviyo endpoint designed for exactly this purpose -
 * it stores the subscription against the exact Shopify variant ID, and
 * Klaviyo's own Shopify integration is responsible for detecting when
 * that variant restocks and sending the notification via a "Back in
 * Stock" flow (confirm that flow is turned on in the Klaviyo dashboard -
 * that one step lives outside the theme and can't be verified from here).
 */
window.NotifyWhenAvailable = (function () {
  var KLAVIYO_COMPANY_ID = 'UgA3Va';
  var KLAVIYO_REVISION = '2026-07-15';

  var forms = [];

  function formatLine(label, value) {
    return label + ': ' + value;
  }

  function findModals() {
    return document.querySelectorAll('.nwa-modal');
  }

  function setContext(opts) {
    findModals().forEach(function (modal) {
      var titleEl = modal.querySelector('[data-nwa-product-title]');
      var optionsEl = modal.querySelector('[data-nwa-product-options]');
      var variantInput = modal.querySelector('[data-nwa-variant-id]');

      if (titleEl) titleEl.textContent = opts.productTitle || '';
      if (optionsEl) {
        optionsEl.innerHTML = '';
        (opts.lines || []).forEach(function (line) {
          var row = document.createElement('div');
          row.textContent = line;
          optionsEl.appendChild(row);
        });
      }
      if (variantInput) variantInput.value = opts.variantId || '';

      resetModalState(modal);
    });
  }

  function resetModalState(modal) {
    var form = modal.querySelector('[data-nwa-form]');
    var message = modal.querySelector('[data-nwa-message]');
    if (form) form.hidden = false;
    if (message) {
      message.hidden = true;
      message.textContent = '';
      message.classList.remove('nwa-modal__message--error');
    }
  }

  function handleSubmit(evt) {
    evt.preventDefault();
    var form = evt.currentTarget;
    var modal = form.closest('.nwa-modal');
    var emailInput = form.querySelector('[data-nwa-email]');
    var variantInput = form.querySelector('[data-nwa-variant-id]');
    var submitBtn = form.querySelector('[data-nwa-submit]');
    var message = modal.querySelector('[data-nwa-message]');

    var email = (emailInput.value || '').trim();
    var variantId = variantInput.value;

    if (!email || !variantId) return;

    submitBtn.disabled = true;
    var originalText = submitBtn.textContent;
    submitBtn.textContent = 'Submitting...';

    var body = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          channels: ['EMAIL'],
          profile: {
            data: {
              type: 'profile',
              attributes: { email: email }
            }
          }
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: '$shopify:::$default:::' + variantId
            }
          }
        }
      }
    };

    fetch('https://a.klaviyo.com/client/back-in-stock-subscriptions?company_id=' + KLAVIYO_COMPANY_ID, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        Accept: 'application/vnd.api+json',
        revision: KLAVIYO_REVISION
      },
      body: JSON.stringify(body)
    })
      .then(function (res) {
        if (res.ok) {
          form.hidden = true;
          message.hidden = false;
          message.classList.remove('nwa-modal__message--error');
          message.textContent = "You're all set - we'll email you as soon as this is back in stock.";
          return;
        }
        return res
          .json()
          .catch(function () {
            return null;
          })
          .then(function (data) {
            var detail =
              data && data.errors && data.errors[0] && data.errors[0].detail
                ? data.errors[0].detail
                : 'Something went wrong. Please try again.';
            throw new Error(detail);
          });
      })
      .catch(function (err) {
        message.hidden = false;
        message.classList.add('nwa-modal__message--error');
        message.textContent = err.message || 'Something went wrong. Please try again.';
      })
      .finally(function () {
        submitBtn.disabled = false;
        submitBtn.textContent = originalText;
      });
  }

  function init() {
    document.querySelectorAll('.nwa-modal [data-nwa-form]').forEach(function (form) {
      if (forms.indexOf(form) !== -1) return;
      forms.push(form);
      form.addEventListener('submit', handleSubmit);
    });
  }

  /**
   * Generic trigger wiring for pages that don't drive setContext()
   * themselves via JS (e.g. the standard product page, whose buy-buttons
   * are re-rendered by the theme's own variant-swap system rather than a
   * page-specific controller like Sunfade's). Any button anywhere with
   * data-nwa-trigger + data-nwa-product-title/-lines/-variant-id syncs
   * the modal to itself right before it opens, reading whatever the
   * currently-rendered attributes say - always correct for the variant
   * on screen at click time, no matter how the surrounding markup got
   * there or how often it was swapped.
   */
  document.addEventListener('click', function (evt) {
    var trigger = evt.target.closest('[data-nwa-trigger]');
    if (!trigger) return;
    setContext({
      productTitle: trigger.dataset.nwaProductTitle || '',
      lines: trigger.dataset.nwaLines ? trigger.dataset.nwaLines.split('|') : [],
      variantId: trigger.dataset.nwaVariantId || ''
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  return { setContext: setContext };
})();
